// /amazon-overview/needed - everything needed from Amazon on Site details' top sites (every
// Amazon site, for InRange, on ?sites=all), one list per site: who owns each item, what
// Amazon has said on it, and its history; filtered by person and by status.
// docs/Y2026-Q4/design-amazon-open-issues.md, design-landlord-follow-up-and-finished-stages.md,
// design-amazon-item-owners.md
import { type ReactNode, useContext, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";

import { approvedResolved, INRANGE, itemId, useAmazonChanges } from "../api/hooks/useAmazonOwners";
import type { AmazonBuilding } from "../api/hooks/useAmazonOverview";
import { useReportView } from "../api/hooks/useShares";
import { UserContext } from "../auth";
import { GapTag } from "../components/GapTag";
import { PersonSwitcher } from "../components/PersonSwitcher";
import { ReviewProvider } from "../components/ReviewProvider";
import { Tooltip } from "../components/Tooltip";
import { TOP } from "./amazonOverview/BuildingView";
import { BulkBar } from "./amazonOverview/BulkBar";
import { FilterSelect } from "./amazonOverview/FilterSelect";
import { formatDate, today } from "./amazonOverview/dates";
import { HoldTag } from "./amazonOverview/HoldTag";
import { OverviewHeading, WhenLoaded } from "./amazonOverview/Frame";
import {
  AnswerBlock,
  ItemActions,
  type ItemRef,
  OwnerChip,
  PersonMark,
  useOwners,
} from "./amazonOverview/ItemOwners";
import {
  blocksOf,
  byName,
  categoriesOf,
  ITEM_STATES,
  type ItemState,
  type ListedItem,
  neededItems,
  type SiteItems,
  type SortBy,
} from "./amazonOverview/openIssues";
import { blocksItsStep, itemReviewAttrs } from "./amazonOverview/needs";
import { pageLabel } from "./amazonOverview/overviewPages";
import { SinceLastLogin } from "./amazonOverview/SinceLastLogin";
import { dismissedFor, rememberDismissed } from "./amazonOverview/sinceLastLoginSeen";
import { siteCode } from "./amazonOverview/siteCode";
import { EVERY_SITE, SITES_PARAM, sitesListed, useEverySite } from "./amazonOverview/siteScope";
import type { useShownBuildings } from "./amazonOverview/useShownBuildings";
import overview from "./amazonOverview/amazonOverview.module.css";
import styles from "./amazonOverview/openIssues.module.css";

const PERSON_PARAM = "person";
const EVERYONE = "everyone";
const UNASSIGNED = "unassigned";

const count = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

type Loaded = ReturnType<typeof useShownBuildings>;

const TOP_LABEL = `Top ${TOP} sites`;

/** Which sites the list covers, in the heading: a switcher for InRange, as My actions'
 * person switcher (Noaa, 25 Oct 2026); plain words for anyone else. */
const SitesSwitcher = ({ everySite, total }: { everySite: boolean; total: number }) => {
  const [, setParams] = useSearchParams();
  return (
    <PersonSwitcher
      title={`(${everySite ? "All sites" : TOP_LABEL})`}
      current={everySite ? EVERY_SITE : ""}
      onChoose={(key) =>
        setParams(
          (current) => {
            const next = new URLSearchParams(current);
            if (key === EVERY_SITE) next.set(SITES_PARAM, EVERY_SITE);
            else next.delete(SITES_PARAM);
            return next;
          },
          { replace: false },
        )
      }
      sections={[
        [
          { key: "", label: TOP_LABEL, count: count(Math.min(TOP, total), "site", "sites") },
          { key: EVERY_SITE, label: "All sites", count: count(total, "site", "sites") },
        ],
      ]}
      wrap={(button) => <span className={overview.heading}>{button}</span>}
    />
  );
};

export const AmazonNeededRoute = () => {
  const loaded = useOutletContext<Loaded>();
  const everySite = useEverySite();
  const { role } = useContext(UserContext);
  useReportView("amazon-overview", pageLabel("needed"));
  return (
    <>
      {role === "admin" ? (
        <OverviewHeading
          title={pageLabel("needed")}
          switcher={<SitesSwitcher everySite={everySite} total={loaded.shown.length} />}
        />
      ) : (
        <OverviewHeading title={`${pageLabel("needed")} (${TOP_LABEL})`} />
      )}
      {loaded.blueprint.isLoading ? (
        <p className={overview.muted}>Loading the shared blueprint…</p>
      ) : (
        <WhenLoaded data={loaded}>
          {() => (
            <>
              {loaded.blueprint.error && (
                <p className={overview.error}>
                  Could not load Process overview&apos;s shared edits, so blocks, categories and
                  needed-by dates follow the original path. Reload to try again.
                </p>
              )}
              <NeededList loaded={loaded} everySite={everySite} />
            </>
          )}
        </WhenLoaded>
      )}
    </>
  );
};

/** The project document a site's rows sit in, so a comment on one reaches that site's
 * keeper (design-amazon-item-comments.md). */
const siteDoc = (b: AmazonBuilding) => ({ "data-review-doc": `project:${b.project_id}` });

/** The item as the controls act on it. */
const refOf = (s: SiteItems, i: ListedItem): ItemRef => ({
  projectId: s.building.project_id,
  itemKey: i.itemKey,
  text: i.text,
  siteCode: siteCode(s.building),
  owed: i.state !== "received",
});

const statusLabel = (picked: ItemState[]): string =>
  picked.length === 0
    ? "No status chosen"
    : picked.length === ITEM_STATES.length
      ? "All statuses"
      : ITEM_STATES.filter((s) => picked.includes(s.state))
          .map((s) => s.label)
          .join(" + ");

/** Not received for an open stage, and holding that stage up. */
const holdsItUp = (i: ListedItem): boolean => i.state === "open" && blocksItsStep(i.need);

const DueCell = ({ i }: { i: ListedItem }): ReactNode => {
  if (i.state === "received")
    return (
      <>
        <span className={styles.received}>Received</span>
        {(i.receivedOn || i.receivedFrom) && (
          <div className={styles.lateBy}>
            {[i.receivedOn && formatDate(new Date(i.receivedOn)), i.receivedFrom && `from ${i.receivedFrom}`]
              .filter(Boolean)
              .join(" ")}
          </div>
        )}
      </>
    );
  if (i.state === "finished")
    return (
      <>
        <span className={styles.due}>
          {i.doneOn ? `Stage done ${formatDate(i.doneOn)}` : "Stage done"}
        </span>
        <div className={styles.lateBy}>{i.partial ? "Partially received" : "Never received"}</div>
      </>
    );
  const blocking = holdsItUp(i) && (
    <div className={overview.tagLine}>
      <HoldTag need={i.need} label="Blocking" />
    </div>
  );
  if (i.neededBy === null)
    return (
      <>
        -{blocking}
      </>
    );
  // How late and why that date, on hover rather than under the chip (Noaa, 22 Oct 2026).
  const date = (
    <span className={i.daysLate === null ? styles.due : styles.dueLate} tabIndex={0}>
      {formatDate(i.neededBy)}
    </span>
  );
  return (
    <>
      {i.daysLate !== null || i.reason ? (
        <Tooltip
          content={
            <>
              {i.daysLate !== null && <div>Late, {count(i.daysLate, "day", "days")}</div>}
              {i.reason && <div>{i.reason}</div>}
            </>
          }
        >
          {date}
        </Tooltip>
      ) : (
        date
      )}
      {blocking}
    </>
  );
};

const ItemRow = ({
  site,
  i,
  ticked,
  onTick,
}: {
  site: SiteItems;
  i: ListedItem;
  ticked: boolean;
  onTick: (on: boolean) => void;
}) => {
  const item = refOf(site, i);
  return (
    <tr {...itemReviewAttrs(site.building.site_code, i.need)}>
      <td className={styles.tick}>
        <input
          type="checkbox"
          aria-label={`Select ${i.text}`}
          checked={ticked}
          onChange={(e) => onTick(e.target.checked)}
        />
      </td>
      <td>
        {i.block}
        {i.category && <div className={styles.lateBy}>{i.category}</div>}
      </td>
      <td>
        {i.text}
        {i.partial && i.state !== "received" && (
          <>
            {" "}
            <span className={overview.statusPartial}>Partially received</span>
            <GapTag partial={i.partial} heading="Why it is partially received" />
          </>
        )}
        {/* Under the requirement, not a column of its own; the search still reads it
            (Noaa, 22 Oct 2026). */}
        {i.clause && <div className={styles.clause}>{i.clause}</div>}
        <AnswerBlock item={item} />
      </td>
      <td className={styles.date}>
        <DueCell i={i} />
      </td>
      <td>
        <OwnerChip item={item} />
      </td>
      <td>
        <ItemActions item={item} layout="column" />
      </td>
    </tr>
  );
};

/** Every word of the search appears in the item's words, block, category, clause or site. */
const matches = (s: SiteItems, i: ListedItem, words: string[]): boolean => {
  if (words.length === 0) return true;
  const hay = [i.text, i.block, i.category, i.clause, s.building.name, siteCode(s.building)]
    .join(" ")
    .toLowerCase();
  return words.every((w) => hay.includes(w));
};

const NeededList = ({ loaded, everySite }: { loaded: Loaded; everySite: boolean }) => {
  const o = useOwners();
  const [params, setParams] = useSearchParams();
  const [statuses, setStatuses] = useState<ItemState[]>(["open"]);
  // Sites, blocks and categories picked; none picked shows every one (Noaa, 26 Oct 2026).
  const [pickedSites, setSites] = useState<string[]>([]);
  const [pickedBlocks, setBlocks] = useState<string[]>([]);
  const [pickedCategories, setCategories] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortBy>("cod");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [blockingOnly, setBlockingOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [ticked, setTicked] = useState<Set<string>>(() => new Set());
  // The kinds of change the list is filtered to, from the banner; none, unfiltered.
  const [changeKinds, setChangeKinds] = useState<Set<string>>(() => new Set());
  const [bannerGone, setBannerGone] = useState(false);
  const now = useMemo(today, []);
  // The review routes refuse a viewer outside InRange, and their payload names no keeper.
  const { role, user, viewingAs } = useContext(UserContext);
  const external = role === "external";
  // Whose banner it is: in View as, the person viewed, whose dismissal is not kept.
  const bannerFor = viewingAs?.email ?? user?.email ?? "";
  const changes = useAmazonChanges(external);
  const changeOf = useMemo(
    () =>
      new Map(
        (changes.data?.items ?? []).map((c) => [itemId(c.project_id, c.item_key), c.changes]),
      ),
    [changes.data],
  );
  // Site details' top sites, the earliest committed COD first (Noaa, 19 Oct 2026), or every
  // site.
  const sites = useMemo(() => sitesListed(loaded.shown, everySite), [loaded.shown, everySite]);
  const listed = useMemo(
    () => neededItems(sites, loaded.steps, sortBy, now),
    [sites, loaded.steps, sortBy, now],
  );
  // An answer InRange approved reads as received at once, before its keeper records it.
  const all = useMemo(
    () =>
      listed.map((s) => ({
        ...s,
        items: s.items.map((i): ListedItem => {
          const answer = o.answerOf(refOf(s, i));
          if (i.state === "received" || !approvedResolved(answer) || !answer) return i;
          const submitted = answer.events[0];
          const approved = answer.events.find((e) => e.kind === "approved");
          return {
            ...i,
            state: "received",
            neededBy: null,
            daysLate: null,
            receivedOn: approved?.at.slice(0, 10) ?? "",
            receivedFrom: submitted?.name ?? "",
          };
        }),
      })),
    [listed, o],
  );

  const me = o.owners?.me ?? null;
  const person = params.get(PERSON_PARAM) ?? (o.isStaff || !me ? EVERYONE : me);
  const changedOn = (s: SiteItems, i: ListedItem) =>
    changeOf.get(itemId(s.building.project_id, i.itemKey));
  const choose = (key: string) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set(PERSON_PARAM, key);
        return next;
      },
      { replace: false },
    );

  const siteOptions = useMemo(() => all.map((s) => s.building).sort(byName), [all]);
  const every = useMemo(() => all.flatMap((s) => s.items), [all]);
  const blocks = useMemo(() => blocksOf(every, loaded.steps), [every, loaded.steps]);
  const categories = useMemo(() => categoriesOf(every, loaded.steps), [every, loaded.steps]);
  // A choice the data no longer holds (a box renamed on Process overview meanwhile) drops
  // out; with none left, the filter reads as all.
  const siteChoices = siteOptions.map((b) => ({ value: b.project_id, label: b.name }));
  const blockChoices = blocks.map((b) => ({ value: b, label: b }));
  const categoryChoices = categories.map((c) => ({ value: c, label: c }));
  const sitesOn = pickedSites.filter((id) => siteOptions.some((b) => b.project_id === id));
  const blocksOn = pickedBlocks.filter((b) => blocks.includes(b));
  const categoriesOn = pickedCategories.filter((c) => categories.includes(c));
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const inScope = (s: SiteItems, i: ListedItem) =>
    matches(s, i, words) &&
    (sitesOn.length === 0 || sitesOn.includes(s.building.project_id)) &&
    (blocksOn.length === 0 || blocksOn.includes(i.block)) &&
    (categoriesOn.length === 0 || categoriesOn.includes(i.category));
  // The changed items in scope, each's changes newest first: what the banner counts, and
  // so exactly what each of its counts lists.
  const onPage = all.flatMap((s) =>
    s.items.flatMap((i) => {
      const c = inScope(s, i) ? changedOn(s, i) : undefined;
      return c ? [c] : [];
    }),
  );
  // A kind the banner no longer counts (the list moved meanwhile) no longer filters.
  const counted = new Set(onPage.map((c) => c[0].kind));
  const activeKinds = new Set([...changeKinds].filter((kind) => counted.has(kind)));
  const changedOnly = activeKinds.size > 0;
  const picked = (s: SiteItems, i: ListedItem) => {
    const c = changedOn(s, i);
    return !!c && activeKinds.has(c[0].kind);
  };
  const ownedBy = (s: SiteItems, i: ListedItem): boolean => {
    // What changed is on their own items, whichever person the list is on.
    if (person === EVERYONE || changedOnly) return true;
    const owner = o.ownerOf(refOf(s, i));
    return person === UNASSIGNED ? owner === null : owner === person;
  };
  const scoped = all.map((s) => ({
    ...s,
    items: s.items.filter((i) => inScope(s, i) && ownedBy(s, i)),
  }));
  const counts = Object.fromEntries(
    ITEM_STATES.map((x) => [x.state, scoped.reduce((n, s) => n + s.items.filter((i) => i.state === x.state).length, 0)]),
  ) as Record<ItemState, number>;
  const overdue = scoped.reduce(
    (n, s) => n + s.items.filter((i) => statuses.includes(i.state) && i.daysLate !== null).length,
    0,
  );
  const blockingCount = scoped.reduce(
    (n, s) => n + s.items.filter((i) => statuses.includes(i.state) && holdsItUp(i)).length,
    0,
  );
  const shown = scoped
    .map((s) => ({
      ...s,
      items: s.items.filter((i) =>
        changedOnly
          ? picked(s, i)
          : statuses.includes(i.state) &&
            (!overdueOnly || i.daysLate !== null) &&
            (!blockingOnly || holdsItUp(i)),
      ),
    }))
    .filter((s) => s.items.length > 0);
  const rows = shown.reduce((n, s) => n + s.items.length, 0);
  // The actions act on what is ticked and still in view; a filter hides, never acts.
  const inView = shown.flatMap((s) => s.items.map((i) => refOf(s, i)));
  const selected = inView.filter((r) => ticked.has(itemId(r.projectId, r.itemKey)));
  const allTicked = inView.length > 0 && selected.length === inView.length;
  const tick = (ids: string[], on: boolean) =>
    setTicked((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  // Each person's open items, as the person filter counts them.
  const openOf = (key: string) =>
    every.length === 0
      ? 0
      : all.reduce(
          (n, s) =>
            n +
            s.items.filter((i) => {
              if (i.state === "received") return false;
              const owner = o.ownerOf(refOf(s, i));
              return key === EVERYONE || (key === UNASSIGNED ? owner === null : owner === key);
            }).length,
          0,
        );
  const people = o.owners?.people ?? [];
  const nameFor = (key: string): string =>
    key === EVERYONE
      ? "Everyone's items"
      : key === UNASSIGNED
        ? "Unassigned items"
        : key === INRANGE
          ? "Assigned to InRange"
          : key === me
            ? "My items"
            : `${o.nameOf(key)}'s items`;

  // The person filter: everyone and the unassigned, the people on the list, then InRange.
  const option = (key: string) => ({
    key,
    label: (
      <span className={styles.personOption}>
        <PersonMark
          id={key === UNASSIGNED ? null : key}
          name={key === EVERYONE || key === UNASSIGNED ? "" : o.nameOf(key)}
        />
        {key === EVERYONE
          ? "Everyone"
          : key === UNASSIGNED
            ? "Unassigned"
            : `${o.nameOf(key)}${key === me ? " (you)" : ""}`}
      </span>
    ),
    count: `${openOf(key)} open`,
  });

  return (
    <>
      {o.failed && (
        <p className={overview.error}>Could not load who owns each item. Reload to try again.</p>
      )}
      {changes.data?.since && !bannerGone && !dismissedFor(bannerFor, changes.data.since) && (
        <SinceLastLogin
          since={changes.data.since}
          latest={onPage.map((c) => c[0])}
          picked={activeKinds}
          onPick={setChangeKinds}
          onDismiss={() => {
            const since = changes.data?.since;
            if (since && !viewingAs) rememberDismissed(bannerFor, since);
            setBannerGone(true);
            setChangeKinds(new Set());
          }}
        />
      )}
      {changes.isError && (
        <p className={overview.error}>
          Could not load what changed since your last login. Reload to try again.
        </p>
      )}
      <div className={styles.toolbar}>
        <PersonSwitcher
          title={nameFor(person)}
          current={person}
          onChoose={choose}
          sections={[
            [EVERYONE, UNASSIGNED].map(option),
            people.map((p) => p.person_id).map(option),
            [option(INRANGE)],
          ]}
          wrap={(button) => <span className={`${styles.select} ${styles.personFilter}`}>{button}</span>}
        />
        <FilterSelect
          name="Site"
          options={siteChoices}
          picked={sitesOn}
          onChange={setSites}
          all="All sites"
        />
        <FilterSelect
          name="Block"
          options={blockChoices}
          picked={blocksOn}
          onChange={setBlocks}
          all="All blocks"
        />
        <FilterSelect
          name="Category"
          options={categoryChoices}
          picked={categoriesOn}
          onChange={setCategories}
          all="All categories"
        />
        {/* Not received for open stages alone by default (Noaa, 21 Oct 2026). */}
        {!changedOnly && (
          <FilterSelect
            name="Status"
            summary={statusLabel(statuses)}
            options={ITEM_STATES.map((x) => ({
              value: x.state,
              label: x.label,
              count: counts[x.state],
            }))}
            picked={statuses}
            onChange={setStatuses}
          />
        )}
        {!changedOnly && (
          <button
            type="button"
            className={styles.chip}
            aria-pressed={overdueOnly}
            onClick={() => setOverdueOnly(!overdueOnly)}
          >
            Overdue ({overdue})
          </button>
        )}
        {!changedOnly && (
          <button
            type="button"
            className={styles.chip}
            aria-pressed={blockingOnly}
            onClick={() => setBlockingOnly(!blockingOnly)}
          >
            Blocking ({blockingCount})
          </button>
        )}
        {/* Which statuses the count covers, so it is not read against the person menu's
            open count, which takes every status but Received (Noaa, 22 Oct 2026). */}
        <span className={overview.muted}>
          {count(rows, "item", "items")}
          {!changedOnly && ` · ${statusLabel(statuses)}`}
        </span>
        <div className={styles.toolbarEnd}>
          <div className={styles.searchBox}>
            <input
              type="search"
              aria-label="Search"
              className={`${styles.select} ${styles.search}`}
              placeholder="Search items"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className={styles.searchClear}
                aria-label="Clear search"
                onClick={() => setSearch("")}
              >
                ×
              </button>
            )}
          </div>
          <select
            id="needed-sort"
            aria-label="Sort"
            className={styles.select}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
          >
            <option value="cod">Sort by COD</option>
            <option value="name">Sort by site name</option>
          </select>
        </div>
      </div>
      {statuses.includes("finished") && (
        <p className={styles.note}>
          Not received, finished stages: items never received, or only partly, for a stage
          already marked done. They hold nothing up and none is late.
        </p>
      )}
      <BulkBar
        selected={selected}
        onDone={(changed) => tick([...changed], false)}
        onClear={() => setTicked(new Set())}
      />
      {rows === 0 ? (
        <p className={overview.muted}>
          {person !== EVERYONE ? "Nothing here matches this person and these filters." : "No item matches these filters."}
        </p>
      ) : (
        <ReviewProvider
          available={!external}
          defaultDoc={null}
          defaultLevel="amazon_item"
          comments={[]}
          projectId={null}
          onQuote={null}
        >
        <div className={overview.tableScroll}>
          <table className={styles.table}>
            <colgroup>
              <col className={styles.colTick} />
              <col className={styles.colBlock} />
              <col className={styles.colText} />
              <col />
              <col className={styles.colOwner} />
              <col className={styles.colActions} />
            </colgroup>
            <thead>
              <tr>
                <th className={styles.tick}>
                  <input
                    type="checkbox"
                    aria-label="Select all items in view"
                    checked={allTicked}
                    ref={(box) => {
                      if (box) box.indeterminate = selected.length > 0 && !allTicked;
                    }}
                    onChange={(e) =>
                      tick(
                        inView.map((r) => itemId(r.projectId, r.itemKey)),
                        e.target.checked,
                      )
                    }
                  />
                </th>
                <th>Block</th>
                <th>Needed information</th>
                <th className={styles.date}>Needed by</th>
                <th>Assigned to</th>
                <th>Actions</th>
              </tr>
            </thead>
            {shown.map((s) => (
              <tbody key={s.building.project_id} {...siteDoc(s.building)}>
                <tr className={styles.siteRow}>
                  <th colSpan={6} scope="rowgroup">
                    {s.building.name}
                    <span className={styles.siteFacts}>
                      {s.cod ? `COD ${formatDate(s.cod)}` : "No committed COD"} ·{" "}
                      {count(s.items.length, "item", "items")}
                    </span>
                  </th>
                </tr>
                {s.items.map((i) => {
                  const id = itemId(s.building.project_id, i.itemKey);
                  return (
                    <ItemRow
                      key={i.key}
                      site={s}
                      i={i}
                      ticked={ticked.has(id)}
                      onTick={(on) => tick([id], on)}
                    />
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
        </ReviewProvider>
      )}
    </>
  );
};
